import { useState, useEffect, useCallback } from "react";
import { Save, RotateCcw, Search, Sliders, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DENSITIES = ["light", "medium", "heavy", "dense"];

const numField = (v) => (v === "" || v === null || v === undefined ? "" : v);

export default function PricingTuner({ token }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/pricing`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setCfg(data.config);
    } catch (e) {
      setError("Could not load pricing config.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/pricing`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ config: cfg }),
      });
      if (!res.ok) throw new Error("save failed");
      const data = await res.json();
      setCfg(data.config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      setError("Save failed. Check your values and try again.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all pricing to the built-in defaults? This can't be undone.")) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/pricing/reset`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCfg(data.config);
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <p className="py-10 text-center text-charcoal/45">Loading pricing…</p>;

  const setGlobal = (k, v) => setCfg((c) => ({ ...c, [k]: v === "" ? "" : Number(v) }));
  const setDisposal = (k, v) => setCfg((c) => ({ ...c, disposal_density: { ...c.disposal_density, [k]: Number(v) } }));
  const setSurcharge = (k, v) => setCfg((c) => ({ ...c, surcharges: { ...c.surcharges, [k]: Number(v) } }));
  const setMult = (k, v) => setCfg((c) => ({ ...c, multipliers: { ...c.multipliers, [k]: Number(v) / 100 } }));
  const setTier = (i, idx, v) => setCfg((c) => {
    const tiers = c.tiers.map((t) => [...t]);
    tiers[i][idx] = Number(v);
    return { ...c, tiers };
  });
  const addTier = () => setCfg((c) => ({ ...c, tiers: [...c.tiers, [1, 100]] }));
  const removeTier = (i) => setCfg((c) => ({ ...c, tiers: c.tiers.filter((_, j) => j !== i) }));
  const setAnchor = (key, idx, v) => setCfg((c) => {
    const a = { ...c.anchors, [key]: [...c.anchors[key]] };
    a[key][idx] = idx === 3 ? (v === "none" ? null : v) : (idx === 2 ? v : Number(v));
    return { ...c, anchors: a };
  });

  const anchorKeys = Object.keys(cfg.anchors).sort();
  const filtered = anchorKeys.filter((k) => k.includes(query.toLowerCase().replace(/\s+/g, "_")));
  const surchargeOpts = ["none", ...Object.keys(cfg.surcharges)];

  return (
    <div className="space-y-6" data-testid="pricing-tuner">
      {/* sticky action bar */}
      <div className="sticky top-0 z-10 -mx-1 flex items-center justify-between gap-3 rounded-2xl bg-canvas/90 px-3 py-3 backdrop-blur">
        <div className="flex items-center gap-2 text-charcoal/70">
          <Sliders size={18} className="text-violet" />
          <h2 className="font-display text-2xl text-charcoal">Pricing tuner</h2>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-sm font-semibold text-sage" data-testid="pricing-saved">Saved ✓</span>}
          {error && <span className="text-sm text-red-600" data-testid="pricing-error">{error}</span>}
          <button onClick={reset} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-sm font-semibold text-charcoal hover:bg-stone/60 disabled:opacity-50">
            <RotateCcw size={14} /> Reset
          </button>
          <button onClick={save} disabled={saving} data-testid="pricing-save-btn" className="inline-flex items-center gap-2 rounded-full bg-violet px-5 py-2 text-sm font-semibold text-white hover:bg-violet-deep disabled:opacity-60">
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* global knobs */}
      <Section title="Cost & volume model">
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <Num label="Volume scale" hint="catalog cu-yd × this" value={cfg.volume_scale} step="0.05" onChange={(v) => setGlobal("volume_scale", v)} testid="cfg-volume_scale" />
          <Num label="Cost base ($)" value={cfg.cost_base} onChange={(v) => setGlobal("cost_base", v)} testid="cfg-cost_base" />
          <Num label="Cost slope ($/full)" value={cfg.cost_slope} onChange={(v) => setGlobal("cost_slope", v)} testid="cfg-cost_slope" />
          <Num label="Min profit ($)" value={cfg.min_profit} onChange={(v) => setGlobal("min_profit", v)} testid="cfg-min_profit" />
          <Num label="Disposal · light" value={cfg.disposal_density.light} onChange={(v) => setDisposal("light", v)} />
          <Num label="Disposal · mixed" value={cfg.disposal_density.mixed} onChange={(v) => setDisposal("mixed", v)} />
          <Num label="Disposal · heavy" value={cfg.disposal_density.heavy} onChange={(v) => setDisposal("heavy", v)} />
        </div>
      </Section>

      {/* tiers */}
      <Section title="Truck volume tiers">
        <div className="space-y-2">
          {cfg.tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`tier-row-${i}`}>
              <span className="w-6 text-xs text-charcoal/40">{i + 1}</span>
              <label className="flex items-center gap-2 text-sm text-charcoal/60">
                ≤ <input type="number" step="0.1" value={numField(t[0])} onChange={(e) => setTier(i, 0, e.target.value)} className="w-24 rounded-xl border border-stone bg-white/70 px-3 py-1.5 outline-none focus:border-violet" /> cu yd
              </label>
              <label className="flex items-center gap-2 text-sm text-charcoal/60">
                $ <input type="number" value={numField(t[1])} onChange={(e) => setTier(i, 1, e.target.value)} className="w-28 rounded-xl border border-stone bg-white/70 px-3 py-1.5 font-semibold text-charcoal outline-none focus:border-violet" />
              </label>
              <button onClick={() => removeTier(i)} className="text-charcoal/30 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
          <button onClick={addTier} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-violet hover:underline"><Plus size={14} /> Add tier</button>
        </div>
      </Section>

      {/* surcharges + multipliers */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Disposal surcharges ($)">
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(cfg.surcharges).map(([k, v]) => (
              <Num key={k} label={k.replace(/_/g, " ")} value={v} onChange={(val) => setSurcharge(k, val)} testid={`surcharge-${k}`} />
            ))}
          </div>
        </Section>
        <Section title="Access multipliers (%)">
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(cfg.multipliers).map(([k, v]) => (
              <Num key={k} label={k.replace(/_/g, " ")} value={Math.round(v * 100)} onChange={(val) => setMult(k, val)} testid={`mult-${k}`} suffix="%" />
            ))}
          </div>
        </Section>
      </div>

      {/* anchors */}
      <Section title={`Item anchors (${anchorKeys.length})`}>
        <div className="relative mb-4 max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search items…" data-testid="anchor-search" className="w-full rounded-full border border-stone bg-white/70 py-2 pl-9 pr-4 text-sm outline-none focus:border-violet" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-stone text-xs uppercase tracking-wide text-charcoal/45">
                <th className="py-2 pr-4">Item</th>
                <th className="py-2 pr-4">Price $</th>
                <th className="py-2 pr-4">Cu yd</th>
                <th className="py-2 pr-4">Density</th>
                <th className="py-2 pr-4">Surcharge</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((key) => {
                const [price, cyd, density, scode] = cfg.anchors[key];
                return (
                  <tr key={key} className="border-b border-stone/50" data-testid={`anchor-row-${key}`}>
                    <td className="py-2 pr-4 font-medium text-charcoal">{key.replace(/_/g, " ")}</td>
                    <td className="py-2 pr-4"><input type="number" value={numField(price)} onChange={(e) => setAnchor(key, 0, e.target.value)} className="w-20 rounded-lg border border-stone bg-white/70 px-2 py-1 outline-none focus:border-violet" /></td>
                    <td className="py-2 pr-4"><input type="number" step="0.1" value={numField(cyd)} onChange={(e) => setAnchor(key, 1, e.target.value)} className="w-20 rounded-lg border border-stone bg-white/70 px-2 py-1 outline-none focus:border-violet" /></td>
                    <td className="py-2 pr-4">
                      <select value={density} onChange={(e) => setAnchor(key, 2, e.target.value)} className="rounded-lg border border-stone bg-white/70 px-2 py-1 outline-none focus:border-violet">
                        {DENSITIES.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-4">
                      <select value={scode || "none"} onChange={(e) => setAnchor(key, 3, e.target.value)} className="rounded-lg border border-stone bg-white/70 px-2 py-1 outline-none focus:border-violet">
                        {surchargeOpts.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="rounded-[1.6rem] bg-white/60 p-6 sm:p-7">
    <h3 className="mb-4 font-display text-lg text-charcoal">{title}</h3>
    {children}
  </section>
);

const Num = ({ label, value, onChange, step, hint, suffix, testid }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium capitalize text-charcoal/60">{label}{hint ? <span className="text-charcoal/35"> · {hint}</span> : ""}</span>
    <div className="flex items-center gap-1.5">
      <input type="number" step={step || "1"} value={numField(value)} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-xl border border-stone bg-white/70 px-3 py-2 text-charcoal outline-none focus:border-violet" />
      {suffix && <span className="text-sm text-charcoal/50">{suffix}</span>}
    </div>
  </label>
);
